import React, { useState, useEffect, useCallback } from 'react';
import { DashboardCard, StatsGrid, StatItem } from '../../../shared/ui';
import { sitesV2Api, SiteV2 } from '../../../api/sitesV2Api';
import { questionsV2Api, QuestionV2 } from '../../../api/questionsV2Api';
import { sessionsV2Api, SessionV2 } from '../../../api/sessionsV2Api';
import ManagedPaymentsCard from '../../payments/ui/ManagedPaymentsCard';
import UserPaymentsCard from '../../payments/ui/UserPaymentsCard';
import AllSitesShiftsCard from '../../siteShifts/ui/AllSitesShiftsCard';
import styles from './StatisticsCard.module.css';

interface SiteStatistics {
  site: SiteV2;
  totalQuestions: number;
  openQuestions: number;
  closedQuestions: number;
  activeSessions: number;
  totalSessions: number;
  membersCount: number;
  totalHours: number;
}

const StatisticsCard: React.FC = () => {
  const [sitesStats, setSitesStats] = useState<SiteStatistics[]>([]);
  const [overallStats, setOverallStats] = useState<StatItem[]>([]);
  const [overallStatsLoading, setOverallStatsLoading] = useState(false);

  // Управление состоянием сворачивания карточек
  const [collapsedCards, setCollapsedCards] = useState<Record<string, boolean>>({});

  const loadStatistics = useCallback(async () => {
    try {
      setOverallStatsLoading(true);

      // Загружаем все объекты пользователя
      const sites = await sitesV2Api.getMySites();
      
      // Для каждого объекта собираем статистику
      const statsPromises = sites.map(async (site): Promise<SiteStatistics> => {
        let totalQuestions = 0;
        let openQuestions = 0;
        let closedQuestions = 0;
        let activeSessions = 0;
        let totalSessions = 0;
        let membersCount = 0;
        let totalHours = 0;

        try {
          // Статистика по вопросам
          const questions = await questionsV2Api.getSiteQuestionsAll(site.id);
          totalQuestions = questions.length;
          openQuestions = questions.filter((q: QuestionV2) => !q.closed).length;
          closedQuestions = questions.filter((q: QuestionV2) => q.closed).length;
        } catch (error) {
          console.warn(`Не удалось загрузить вопросы для объекта ${site.id}:`, error);
        }

        try {
          // Статистика по сменам
          const sessions = await sessionsV2Api.getSiteSessions(site.id);
          activeSessions = sessions.filter((s: SessionV2) => !s.timeEnd).length;
          totalSessions = sessions.length;
          
          // Подсчитываем общее количество часов
          sessions.forEach((session: SessionV2) => {
            if (session.timeEnd) {
              const start = new Date(session.timeStart);
              const end = new Date(session.timeEnd);
              const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
              totalHours += hours;
            } else if (session.hours) {
              totalHours += session.hours;
            }
          });
        } catch (error) {
          console.warn(`Не удалось загрузить смены для объекта ${site.id}:`, error);
        }

        try {
          // Статистика по участникам
          const members = await sitesV2Api.getSiteMembers(site.id);
          membersCount = members.filter((m) => m.isActive).length;
        } catch (error) {
          console.warn(`Не удалось загрузить участников для объекта ${site.id}:`, error);
        }

        return {
          site,
          totalQuestions,
          openQuestions,
          closedQuestions,
          activeSessions,
          totalSessions,
          membersCount,
          totalHours
        };
      });

      const stats = await Promise.all(statsPromises);
      setSitesStats(stats);

      // Вычисляем общую статистику
      const totalSites = sites.length;
      const activeSites = sites.filter(s => !s.closed).length;
      const totalQuestions = stats.reduce((sum, s) => sum + s.totalQuestions, 0);
      const openQuestions = stats.reduce((sum, s) => sum + s.openQuestions, 0);
      const totalSessions = stats.reduce((sum, s) => sum + s.totalSessions, 0);
      const activeSessions = stats.reduce((sum, s) => sum + s.activeSessions, 0);
      const totalHours = Math.round(stats.reduce((sum, s) => sum + s.totalHours, 0) * 10) / 10;
      const totalMembers = stats.reduce((sum, s) => sum + s.membersCount, 0);

      // Подсчитываем уникальных сотрудников на всех объектах
      // TODO: Позже реализовать правильный подсчет уникальных сотрудников
      // Сейчас просто примерная статистика
      const uniqueMembersCount = totalMembers; // TODO: подсчитать уникальных сотрудников

      setOverallStats([
        {
          id: 'sites',
          value: totalSites,
          label: 'Объектов',
          icon: 'ri-building-line',
          color: 'muted'
        },
        {
          id: 'active-sites',
          value: activeSites,
          label: 'Активных',
          icon: 'ri-checkbox-circle-line',
          color: 'success'
        },
        {
          id: 'unique-members',
          value: uniqueMembersCount,
          label: 'Уникальных сотрудников',
          icon: 'ri-user-line',
          color: 'muted'
        },
        {
          id: 'questions',
          value: totalQuestions,
          label: 'Дискуссий',
          icon: 'ri-question-line',
          color: 'muted'
        },
        {
          id: 'open-questions',
          value: openQuestions,
          label: 'Открытых',
          icon: 'ri-questionnaire-line',
          color: 'info'
        },
        {
          id: 'sessions',
          value: totalSessions,
          label: 'Смен',
          icon: 'ri-time-line',
          color: 'muted'
        },
        {
          id: 'active-sessions',
          value: activeSessions,
          label: 'Активных',
          icon: 'ri-play-circle-line',
          color: 'success'
        },
        {
          id: 'hours',
          value: totalHours,
          label: 'Часов',
          icon: 'ri-timer-line',
          color: 'muted'
        }
      ]);
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    } finally {
      setOverallStatsLoading(false);
    }
  }, []);

  // Ленивая загрузка: загружаем общую статистику только при раскрытии карточки
  const isOverallStatsExpanded = !(collapsedCards['overall-statistics-card'] ?? true);
  useEffect(() => {
    if (isOverallStatsExpanded) {
      loadStatistics();
    }
  }, [isOverallStatsExpanded, loadStatistics]);

  const toggleCard = (cardId: string) => {
    setCollapsedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  return (
    <div className={styles.statisticsContainer}>
      {/* Общая статистика — данные грузятся только при раскрытии карточки */}
      <DashboardCard
        id="overall-statistics-card"
        title="Общая статистика"
        icon="ri-bar-chart-line"
        isCollapsible={true}
        isCollapsed={collapsedCards['overall-statistics-card'] ?? true}
        onToggleCollapse={() => toggleCard('overall-statistics-card')}
      >
        {overallStatsLoading ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}>
              <i className="ri-loader-4-line"></i>
            </div>
            <span>Загрузка статистики...</span>
          </div>
        ) : (
          <StatsGrid items={overallStats} />
        )}
      </DashboardCard>

      {/* Общие смены со всех объектов */}
      <AllSitesShiftsCard />

      {/* Общие платежи сотрудникам */}
      <ManagedPaymentsCard />

      {/* Мои платежи */}
      <UserPaymentsCard />

    </div>
  );
};

export default StatisticsCard;